/**
 * Billing & usage — the manage/view surface, reached from the profile menu.
 *
 * Shows the plan/tier and token usage (`/subscriptions/me`), a link to the
 * provider's own portal (`/billing/portal`), and this account's payment history
 * (`/billing/intents`) with per-payment receipts. Plain hooks — the add-in has
 * no TanStack — mirroring the web `BillingPage`.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, useCallback, useEffect, useState } from "react";

import { createPortalSession, getMyQuota, listIntents, type PaymentIntent } from "../../api/client";
import { openExternal } from "../services/openExternal";
import { fonts, inkColors } from "../tokens";
import { describePurchase, hasMore, paidAmount } from "./payments";
import ReceiptDialog from "./ReceiptDialog";

const PAGE_SIZE = 25;

const useStyles = makeStyles({
  surface: { maxWidth: "440px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "14px" },
  section: { display: "flex", flexDirection: "column", gap: "6px" },
  heading: {
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink3,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  planRow: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink },
  muted: { fontFamily: fonts.body, fontSize: "11.5px", color: inkColors.ink3 },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "8px 0",
    borderBottom: "1px solid var(--line)",
  },
  rowMain: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  rowTitle: {
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowMeta: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3 },
  rowRight: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
});

type Quota = Awaited<ReturnType<typeof getMyQuota>>;

export const BillingDialog: FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const styles = useStyles();
  const [quota, setQuota] = useState<Quota | null>(null);
  const [items, setItems] = useState<PaymentIntent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNote, setPortalNote] = useState<string | null>(null);
  const [receiptRef, setReceiptRef] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void getMyQuota()
      .then((q) => {
        if (live) setQuota(q);
      })
      .catch(() => {
        /* usage is a nicety here; a failure just hides the row */
      });
    return () => {
      live = false;
    };
  }, []);

  const loadPage = useCallback(async (offset: number) => {
    setLoading(true);
    try {
      const page = await listIntents({ limit: PAGE_SIZE, offset });
      setTotal(page.total);
      setItems((prev) => (offset === 0 ? [...page.items] : [...prev, ...page.items]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage(0);
  }, [loadPage]);

  const openPortal = useCallback(async () => {
    setPortalBusy(true);
    setPortalNote(null);
    try {
      const { url } = await createPortalSession();
      openExternal(url);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setPortalNote(
        status === 503 ? "No payment portal is available on this deployment." : "Could not open the payment portal.",
      );
    } finally {
      setPortalBusy(false);
    }
  }, []);

  return (
    <>
      <Dialog
        open
        onOpenChange={(_, data) => {
          if (!data.open) onDismiss();
        }}
      >
        <DialogSurface className={styles.surface}>
          <DialogBody>
            <DialogTitle>Billing &amp; usage</DialogTitle>
            <DialogContent>
              <div className={styles.body}>
                {quota !== null && (
                  <div className={styles.section}>
                    <span className={styles.heading}>Plan</span>
                    <span className={styles.planRow}>
                      {quota.plan_name ?? quota.tier}
                      {quota.status ? ` · ${quota.status}` : ""}
                    </span>
                    <span className={styles.muted}>
                      {quota.input_tokens_used.toLocaleString()} in · {quota.output_tokens_used.toLocaleString()} out
                      tokens this period
                    </span>
                  </div>
                )}

                <div className={styles.section}>
                  <span className={styles.heading}>Payment method</span>
                  <span className={styles.muted}>
                    We never see or store your card. Dodo Payments is the merchant of record.
                  </span>
                  <div>
                    <Button appearance="secondary" onClick={() => void openPortal()} disabled={portalBusy}>
                      {portalBusy ? "Opening…" : "Manage at Dodo Payments"}
                    </Button>
                  </div>
                  {portalNote !== null && <span className={styles.muted}>{portalNote}</span>}
                </div>

                <div className={styles.section}>
                  <span className={styles.heading}>Payments</span>
                  {items.length === 0 && !loading && <span className={styles.muted}>No payments yet.</span>}
                  {items.map((intent) => (
                    <div className={styles.row} key={intent.ref}>
                      <div className={styles.rowMain}>
                        <span className={styles.rowTitle}>
                          {intent.doc_types[0] ?? "Document"} · {describePurchase(intent)}
                        </span>
                        <span className={styles.rowMeta}>
                          {new Date(intent.created_at).toLocaleDateString()} · {intent.state}
                        </span>
                      </div>
                      <div className={styles.rowRight}>
                        <span className={styles.rowMeta}>{paidAmount(intent)}</span>
                        {(intent.state === "completed" || intent.state === "refunded") && (
                          <Button appearance="transparent" size="small" onClick={() => setReceiptRef(intent.ref)}>
                            Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && <Spinner size="tiny" />}
                  {hasMore(items.length, total) && !loading && (
                    <Button appearance="transparent" size="small" onClick={() => void loadPage(items.length)}>
                      Load older
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={onDismiss}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <ReceiptDialog ref_={receiptRef} onDismiss={() => setReceiptRef(null)} />
    </>
  );
};

export default BillingDialog;
